/**
 * Sector operations — Bookings & Memberships client service.
 * R39 A16.a2 (SO-P74).
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  type Unsubscribe,
  type QueryConstraint,
} from 'firebase/firestore'
import type { Functions } from 'firebase/functions'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import type {
  UpsertCustomerInput,
  ArchiveCustomerInput,
  FindCustomerByPhoneInput,
  LinkMyCustomerRecordInput,
  CreateBookingPlanInput,
  UpdateBookingPlanInput,
  ArchiveBookingPlanInput,
  GrantMembershipInput,
  RenewMembershipInput,
  FreezeMembershipInput,
  UnfreezeMembershipInput,
  CancelMembershipInput,
  RecordDuePaidInput,
  WaiveDueInput,
  CreateBookableInput,
  UpdateBookableInput,
  ArchiveBookableInput,
  BookSessionInput,
  BookInHouseExtraInput,
  CancelBookingInput,
  MoveBookingInput,
  LeaveWaitlistInput,
  WaiveStrikeInput,
  ClearBookingBlockInput,
  IssueQueueTokenInput,
  CallNextTokenInput,
  MarkTokenServedInput,
  SkipTokenInput,
  HoldQueueTokenInput,
  GetQueueBoardInput,
  ListQueueTokensInput,
  GetMyBookingQrInput,
  CheckInByCustomerQrInput,
  BookingCheckInByPlaceQrInput,
  PrepareBookingCheckInOtpInput,
  CheckInByPhoneOtpInput,
  ManualCheckInInput,
  SetPublicBookingLinkInput,
  GetPublicBookingInfoInput,
  PublicBookSessionInput,
  PublicCancelBookingInput,
  ListMyPublicBookingsInput,
  ListRenewalsDueInput,
  ListChurnCallsInput,
  RecordCustomerCallInput,
  OpsCustomer,
  OpsCustomerContact,
  OpsBookingPlan,
  OpsMembership,
  OpsMembershipLedgerEntry,
  OpsBookingDue,
  OpsBookable,
  OpsBookingSession,
  OpsBooking,
  OpsQueueToken,
  OpsPublicBookingLink,
  OpsPublicBookingSettings,
  OpsCustomerCall,
  OpsBookingCheckIn,
  OpsSector,
} from '../types/ops'

export type {
  OpsCustomer,
  OpsCustomerContact,
  OpsBookingPlan,
  OpsMembership,
  OpsMembershipLedgerEntry,
  OpsBookingDue,
  OpsBookable,
  OpsBookingSession,
  OpsBooking,
  OpsQueueToken,
  OpsPublicBookingLink,
  OpsPublicBookingSettings,
  OpsCustomerCall,
  OpsSector,
}

// Customers
export async function upsertCustomer(
  input: UpsertCustomerInput,
): Promise<{ customerId: string; created: boolean }> {
  return callable<UpsertCustomerInput, { customerId: string; created: boolean }>(
    'upsertCustomer',
    input,
  )
}

export async function archiveCustomer(
  input: ArchiveCustomerInput,
): Promise<{ customerId: string; archived: boolean }> {
  return callable<ArchiveCustomerInput, { customerId: string; archived: boolean }>(
    'archiveCustomer',
    input,
  )
}

export async function findCustomerByPhone(
  input: FindCustomerByPhoneInput,
): Promise<{ customer: OpsCustomer | null; contact: OpsCustomerContact | null }> {
  return callable<FindCustomerByPhoneInput, { customer: OpsCustomer | null; contact: OpsCustomerContact | null }>(
    'findCustomerByPhone',
    input,
  )
}

export async function linkMyCustomerRecord(
  input: LinkMyCustomerRecordInput,
): Promise<{ linked: boolean; customerId: string | null }> {
  return callable<LinkMyCustomerRecordInput, { linked: boolean; customerId: string | null }>(
    'linkMyCustomerRecord',
    input,
  )
}

// Plans
export async function createBookingPlan(
  input: CreateBookingPlanInput,
): Promise<{ planId: string }> {
  return callable<CreateBookingPlanInput, { planId: string }>('createBookingPlan', input)
}

export async function updateBookingPlan(
  input: UpdateBookingPlanInput,
): Promise<{ planId: string }> {
  return callable<UpdateBookingPlanInput, { planId: string }>('updateBookingPlan', input)
}

export async function archiveBookingPlan(
  input: ArchiveBookingPlanInput,
): Promise<{ planId: string; active: boolean }> {
  return callable<ArchiveBookingPlanInput, { planId: string; active: boolean }>(
    'archiveBookingPlan',
    input,
  )
}

// Memberships & Dues
export async function grantMembership(
  input: GrantMembershipInput,
): Promise<{ membershipId: string; dueId?: string }> {
  return callable<GrantMembershipInput, { membershipId: string; dueId?: string }>(
    'grantMembership',
    input,
  )
}

export async function renewMembership(
  input: RenewMembershipInput,
): Promise<{ membershipId: string; renewedFromId: string; dueId?: string }> {
  return callable<RenewMembershipInput, { membershipId: string; renewedFromId: string; dueId?: string }>(
    'renewMembership',
    input,
  )
}

export async function freezeMembership(
  input: FreezeMembershipInput,
): Promise<{ membershipId: string; state: string; validTo: string }> {
  return callable<FreezeMembershipInput, { membershipId: string; state: string; validTo: string }>(
    'freezeMembership',
    input,
  )
}

export async function unfreezeMembership(
  input: UnfreezeMembershipInput,
): Promise<{ membershipId: string; state: string }> {
  return callable<UnfreezeMembershipInput, { membershipId: string; state: string }>(
    'unfreezeMembership',
    input,
  )
}

export async function cancelMembership(
  input: CancelMembershipInput,
): Promise<{ membershipId: string; state: string }> {
  return callable<CancelMembershipInput, { membershipId: string; state: string }>(
    'cancelMembership',
    input,
  )
}

export async function recordDuePaid(
  input: RecordDuePaidInput,
): Promise<{ dueId: string; state: string }> {
  return callable<RecordDuePaidInput, { dueId: string; state: string }>('recordDuePaid', input)
}

export async function waiveDue(
  input: WaiveDueInput,
): Promise<{ dueId: string; state: string }> {
  return callable<WaiveDueInput, { dueId: string; state: string }>('waiveDue', input)
}

// Bookables
export async function createBookable(
  input: CreateBookableInput,
): Promise<{ bookableId: string }> {
  return callable<CreateBookableInput, { bookableId: string }>('createBookable', input)
}

export async function updateBookable(
  input: UpdateBookableInput,
): Promise<{ bookableId: string }> {
  return callable<UpdateBookableInput, { bookableId: string }>('updateBookable', input)
}

export async function archiveBookable(
  input: ArchiveBookableInput,
): Promise<{ bookableId: string; active: boolean }> {
  return callable<ArchiveBookableInput, { bookableId: string; active: boolean }>(
    'archiveBookable',
    input,
  )
}

// Bookings
export async function bookSession(
  input: BookSessionInput,
): Promise<{ bookingId: string; state: string; waitlistPosition?: number | null }> {
  return callable<BookSessionInput, { bookingId: string; state: string; waitlistPosition?: number | null }>(
    'bookSession',
    input,
  )
}

export async function bookInHouseExtra(
  input: BookInHouseExtraInput,
): Promise<{ bookingId: string; state: string; waitlistPosition?: number | null }> {
  return callable<BookInHouseExtraInput, { bookingId: string; state: string; waitlistPosition?: number | null }>(
    'bookInHouseExtra',
    input,
  )
}

export async function cancelBooking(
  input: CancelBookingInput,
): Promise<{ bookingId: string; state: string }> {
  return callable<CancelBookingInput, { bookingId: string; state: string }>(
    'cancelBooking',
    input,
  )
}

export async function moveBooking(
  input: MoveBookingInput,
): Promise<{ bookingId: string; success: boolean }> {
  return callable<MoveBookingInput, { bookingId: string; success: boolean }>(
    'moveBooking',
    input,
  )
}

export async function leaveWaitlist(
  input: LeaveWaitlistInput,
): Promise<{ bookingId: string; state: string }> {
  return callable<LeaveWaitlistInput, { bookingId: string; state: string }>(
    'leaveWaitlist',
    input,
  )
}

export async function waiveStrike(
  input: WaiveStrikeInput,
): Promise<{ customerId: string; success: boolean }> {
  return callable<WaiveStrikeInput, { customerId: string; success: boolean }>(
    'waiveStrike',
    input,
  )
}

export async function clearBookingBlock(
  input: ClearBookingBlockInput,
): Promise<{ customerId: string; success: boolean }> {
  return callable<ClearBookingBlockInput, { customerId: string; success: boolean }>(
    'clearBookingBlock',
    input,
  )
}

// Queue Tokens
export async function issueQueueToken(
  input: IssueQueueTokenInput,
): Promise<{ tokenId: string; n: number }> {
  return callable<IssueQueueTokenInput, { tokenId: string; n: number }>(
    'issueQueueToken',
    input,
  )
}

export async function callNextToken(
  input: CallNextTokenInput,
): Promise<{ token: OpsQueueToken | null }> {
  return callable<CallNextTokenInput, { token: OpsQueueToken | null }>(
    'callNextToken',
    input,
  )
}

export async function markTokenServed(
  input: MarkTokenServedInput,
): Promise<{ success: boolean; token: OpsQueueToken }> {
  return callable<MarkTokenServedInput, { success: boolean; token: OpsQueueToken }>(
    'markTokenServed',
    input,
  )
}

export async function skipToken(
  input: SkipTokenInput,
): Promise<{ success: boolean; token: OpsQueueToken }> {
  return callable<SkipTokenInput, { success: boolean; token: OpsQueueToken }>(
    'skipToken',
    input,
  )
}

export async function holdQueueToken(
  input: HoldQueueTokenInput,
): Promise<OpsQueueToken> {
  return callable<HoldQueueTokenInput, OpsQueueToken>(
    'holdQueueToken',
    input,
  )
}

export async function getQueueBoard(
  input: GetQueueBoardInput,
): Promise<{ placeId: string; dayKey: string; tokens: Array<{ n: number; state: string }> }> {
  return callable<GetQueueBoardInput, { placeId: string; dayKey: string; tokens: Array<{ n: number; state: string }> }>(
    'getQueueBoard',
    input,
  )
}

export async function listQueueTokens(
  input: ListQueueTokensInput,
): Promise<{ placeId: string; dayKey: string; tokens: Array<OpsQueueToken & { customerDisplayName?: string }> }> {
  return callable<ListQueueTokensInput, { placeId: string; dayKey: string; tokens: Array<OpsQueueToken & { customerDisplayName?: string }> }>(
    'listQueueTokens',
    input,
  )
}

// QR Attendance
export async function getMyBookingQr(
  input: GetMyBookingQrInput,
): Promise<{ token: string; expiresAt: number }> {
  return callable<GetMyBookingQrInput, { token: string; expiresAt: number }>(
    'getMyBookingQr',
    input,
  )
}

export async function checkInByCustomerQr(
  input: CheckInByCustomerQrInput,
): Promise<{ bookingId: string; state: string; checkIn: OpsBookingCheckIn }> {
  return callable<CheckInByCustomerQrInput, { bookingId: string; state: string; checkIn: OpsBookingCheckIn }>(
    'checkInByCustomerQr',
    input,
  )
}

export async function bookingCheckInByPlaceQr(
  input: BookingCheckInByPlaceQrInput,
): Promise<{ bookingId: string; state: string; checkIn: OpsBookingCheckIn }> {
  return callable<BookingCheckInByPlaceQrInput, { bookingId: string; state: string; checkIn: OpsBookingCheckIn }>(
    'bookingCheckInByPlaceQr',
    input,
  )
}

export async function prepareBookingCheckInOtp(
  input: PrepareBookingCheckInOtpInput,
): Promise<{ success: boolean; bookingId: string; attemptsRemaining: number }> {
  return callable<PrepareBookingCheckInOtpInput, { success: boolean; bookingId: string; attemptsRemaining: number }>(
    'prepareBookingCheckInOtp',
    input,
  )
}

export async function checkInByPhoneOtp(
  input: CheckInByPhoneOtpInput,
): Promise<{ bookingId: string; state: string; checkIn: OpsBookingCheckIn }> {
  return callable<CheckInByPhoneOtpInput, { bookingId: string; state: string; checkIn: OpsBookingCheckIn }>(
    'checkInByPhoneOtp',
    input,
  )
}

export async function manualCheckIn(
  input: ManualCheckInInput,
): Promise<{ bookingId: string; state: string; checkIn: OpsBookingCheckIn }> {
  return callable<ManualCheckInInput, { bookingId: string; state: string; checkIn: OpsBookingCheckIn }>(
    'manualCheckIn',
    input,
  )
}

// Public Booking Link
export async function setPublicBookingLink(
  input: SetPublicBookingLinkInput,
): Promise<{ enabled: boolean; token?: string }> {
  return callable<SetPublicBookingLinkInput, { enabled: boolean; token?: string }>(
    'setPublicBookingLink',
    input,
  )
}

export async function getPublicBookingInfo(
  input: GetPublicBookingInfoInput,
  options?: { functions?: Functions },
): Promise<{ workspaceName: string; logoUrl?: string; bookables: Record<string, unknown>[] }> {
  return callable<GetPublicBookingInfoInput, { workspaceName: string; logoUrl?: string; bookables: Record<string, unknown>[] }>(
    'getPublicBookingInfo',
    input,
    options ? { functions: options.functions } : undefined,
  )
}

export async function publicBookSession(
  input: PublicBookSessionInput,
  options?: { functions?: Functions },
): Promise<{ bookingId: string; state: string }> {
  return callable<PublicBookSessionInput, { bookingId: string; state: string }>(
    'publicBookSession',
    input,
    options ? { functions: options.functions } : undefined,
  )
}

export async function publicCancelBooking(
  input: PublicCancelBookingInput,
  options?: { functions?: Functions },
): Promise<{ ok: boolean; bookingId?: string; state?: string }> {
  return callable<PublicCancelBookingInput, { ok: boolean; bookingId?: string; state?: string }>(
    'publicCancelBooking',
    input,
    options ? { functions: options.functions } : undefined,
  )
}

export async function listMyPublicBookings(
  input: ListMyPublicBookingsInput,
  options?: { functions?: Functions },
): Promise<{ bookings: OpsBooking[] }> {
  return callable<ListMyPublicBookingsInput, { bookings: OpsBooking[] }>(
    'listMyPublicBookings',
    input,
    options ? { functions: options.functions } : undefined,
  )
}

// Desk Lists
export interface DeskListCallInfo {
  outcome: string
  note?: string
  notes?: string
  calledAt: number
  callerUid: string
}

export interface RenewalDueRow {
  membershipId: string
  customerId: string
  customerDisplayName: string
  customerPhone?: string
  validTo: string
  planName: string
  lastCall?: DeskListCallInfo
}

export interface ChurnCallRow {
  customerId: string
  customerDisplayName: string
  customerPhone?: string
  lastAttendedAt?: number
  planNames: string[]
  lastCall?: DeskListCallInfo
}

export async function listRenewalsDue(
  input: ListRenewalsDueInput,
): Promise<{ rows: RenewalDueRow[]; renewals?: RenewalDueRow[] }> {
  return callable<ListRenewalsDueInput, { rows: RenewalDueRow[]; renewals?: RenewalDueRow[] }>(
    'listRenewalsDue',
    input,
  )
}

export async function listChurnCalls(
  input: ListChurnCallsInput,
): Promise<{ rows: ChurnCallRow[]; churnCalls?: ChurnCallRow[] }> {
  return callable<ListChurnCallsInput, { rows: ChurnCallRow[]; churnCalls?: ChurnCallRow[] }>(
    'listChurnCalls',
    input,
  )
}

export async function recordCustomerCall(
  input: RecordCustomerCallInput,
): Promise<{ id: string; customerId: string; outcome: string; note?: string; calledAt: number }> {
  return callable<RecordCustomerCallInput, { id: string; customerId: string; outcome: string; note?: string; calledAt: number }>(
    'recordCustomerCall',
    input,
  )
}

// ---------------------------------------------------------------------------
// Firestore Subscriptions and Reads (R39 A16.b)
// ---------------------------------------------------------------------------

export interface BookablesQueryOptions {
  callerUid?: string
  isRestricted?: boolean
  activeOnly?: boolean
}

export function subscribeBookables(
  workspaceId: string,
  sector: OpsSector,
  onUpdate: (items: OpsBookable[]) => void,
  onError?: (err: Error) => void,
  options?: BookablesQueryOptions,
): Unsubscribe {
  if (!workspaceId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_bookables')

  if (options?.isRestricted && options.callerUid) {
    const callerUid = options.callerUid
    const unsubs: Unsubscribe[] = []
    const groups: OpsBookable[][] = [[], []]

    const emit = () => {
      const map = new Map<string, OpsBookable>()
      for (const group of groups) {
        for (const item of group) {
          map.set(item.id, item)
        }
      }
      const list = Array.from(map.values())
      if (options.activeOnly) {
        onUpdate(list.filter((b) => b.active).sort((a, b) => a.name.localeCompare(b.name)))
      } else {
        onUpdate(list.sort((a, b) => a.name.localeCompare(b.name)))
      }
    }

    const q1 = query(
      coll,
      where('workspaceId', '==', workspaceId),
      where('sector', '==', sector),
      where('readAccess', '==', null),
    )
    const q2 = query(
      coll,
      where('workspaceId', '==', workspaceId),
      where('sector', '==', sector),
      where('readAccess', 'array-contains', callerUid),
    )

    unsubs.push(
      onSnapshot(q1, (snap) => {
        groups[0] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<OpsBookable, 'id'>) }))
        emit()
      }, onError),
    )
    unsubs.push(
      onSnapshot(q2, (snap) => {
        groups[1] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<OpsBookable, 'id'>) }))
        emit()
      }, onError),
    )

    return () => {
      for (const u of unsubs) u()
    }
  }

  const constraints = [
    where('workspaceId', '==', workspaceId),
    where('sector', '==', sector),
  ]
  if (options?.activeOnly) {
    constraints.push(where('active', '==', true))
  }

  const q = query(coll, ...constraints)
  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<OpsBookable, 'id'>) }))
      items.sort((a, b) => a.name.localeCompare(b.name))
      onUpdate(items)
    },
    onError,
  )
}

export async function listBookables(
  workspaceId: string,
  sector: OpsSector,
  options?: BookablesQueryOptions,
): Promise<OpsBookable[]> {
  return new Promise((resolve, reject) => {
    let settled = false
    let cleanup: (() => void) | null = null
    const unsub = subscribeBookables(
      workspaceId,
      sector,
      (items) => {
        settled = true
        if (cleanup) cleanup()
        resolve(items)
      },
      (err) => {
        settled = true
        if (cleanup) cleanup()
        reject(err)
      },
      options,
    )
    cleanup = unsub
    if (settled) {
      unsub()
    }
  })
}

export interface BookingSessionsQueryOptions {
  callerUid?: string
  isRestricted?: boolean
  bookableId?: string
  toDate?: string
}

export function subscribeBookingSessions(
  workspaceId: string,
  sector: OpsSector,
  date: string,
  onUpdate: (items: OpsBookingSession[]) => void,
  onError?: (err: Error) => void,
  options?: BookingSessionsQueryOptions,
): Unsubscribe {
  if (!workspaceId || !date) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_booking_sessions')
  const baseConstraints: QueryConstraint[] = [
    where('workspaceId', '==', workspaceId),
    where('sector', '==', sector),
  ]
  if (options?.toDate) {
    baseConstraints.push(where('date', '>=', date), where('date', '<=', options.toDate))
  } else {
    baseConstraints.push(where('date', '==', date))
  }
  if (options?.bookableId) {
    baseConstraints.push(where('bookableId', '==', options.bookableId))
  }

  const sortSessions = (a: OpsBookingSession, b: OpsBookingSession) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date)
    return a.startTime.localeCompare(b.startTime)
  }

  if (options?.isRestricted && options.callerUid) {
    const callerUid = options.callerUid
    const unsubs: Unsubscribe[] = []
    const groups: OpsBookingSession[][] = [[], []]

    const emit = () => {
      const mergedMap = new Map<string, OpsBookingSession>()
      for (const group of groups) {
        for (const item of group) {
          mergedMap.set(item.id, item)
        }
      }
      const list = Array.from(mergedMap.values())
      list.sort(sortSessions)
      onUpdate(list)
    }

    const q1 = query(coll, ...baseConstraints, where('readAccess', '==', null))
    const q2 = query(coll, ...baseConstraints, where('readAccess', 'array-contains', callerUid))

    unsubs.push(
      onSnapshot(
        q1,
        (snap) => {
          groups[0] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<OpsBookingSession, 'id'>) }))
          emit()
        },
        onError,
      ),
    )
    unsubs.push(
      onSnapshot(
        q2,
        (snap) => {
          groups[1] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<OpsBookingSession, 'id'>) }))
          emit()
        },
        onError,
      ),
    )

    return () => {
      for (const u of unsubs) u()
    }
  }

  const q = query(coll, ...baseConstraints)
  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<OpsBookingSession, 'id'>) }))
      items.sort(sortSessions)
      onUpdate(items)
    },
    onError,
  )
}

export interface BookingsQueryOptions {
  callerUid?: string
  isRestricted?: boolean
}

export function subscribeBookingsBySession(
  workspaceId: string,
  bookableId: string,
  sessionKey: string,
  onUpdate: (items: OpsBooking[]) => void,
  onError?: (err: Error) => void,
  options?: BookingsQueryOptions,
): Unsubscribe {
  if (!workspaceId || !bookableId || !sessionKey) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_bookings')
  const sortBookings = (list: OpsBooking[]) => {
    list.sort((a, b) => {
      if (a.state === 'WAITLISTED' && b.state === 'WAITLISTED') {
        return (a.waitlistPosition ?? 9999) - (b.waitlistPosition ?? 9999)
      }
      return a.startAt - b.startAt
    })
  }

  const baseConstraints = [
    where('workspaceId', '==', workspaceId),
    where('bookableId', '==', bookableId),
    where('sessionKey', '==', sessionKey),
  ]

  if (options?.isRestricted && options.callerUid) {
    const callerUid = options.callerUid
    const unsubs: Unsubscribe[] = []
    const groups: OpsBooking[][] = [[], []]

    const emit = () => {
      const mergedMap = new Map<string, OpsBooking>()
      for (const group of groups) {
        for (const item of group) {
          mergedMap.set(item.id, item)
        }
      }
      const list = Array.from(mergedMap.values())
      sortBookings(list)
      onUpdate(list)
    }

    const q1 = query(coll, ...baseConstraints, where('readAccess', '==', null))
    const q2 = query(coll, ...baseConstraints, where('readAccess', 'array-contains', callerUid))

    unsubs.push(
      onSnapshot(
        q1,
        (snap) => {
          groups[0] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<OpsBooking, 'id'>) }))
          emit()
        },
        onError,
      ),
    )
    unsubs.push(
      onSnapshot(
        q2,
        (snap) => {
          groups[1] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<OpsBooking, 'id'>) }))
          emit()
        },
        onError,
      ),
    )

    return () => {
      for (const u of unsubs) u()
    }
  }

  const q = query(coll, ...baseConstraints)

  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<OpsBooking, 'id'>) }))
      sortBookings(items)
      onUpdate(items)
    },
    onError,
  )
}

export function subscribeBookingsByCustomer(
  workspaceId: string,
  customerId: string,
  onUpdate: (items: OpsBooking[]) => void,
  onError?: (err: Error) => void,
  options?: BookingsQueryOptions,
): Unsubscribe {
  if (!workspaceId || !customerId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_bookings')
  const baseConstraints = [
    where('workspaceId', '==', workspaceId),
    where('customerId', '==', customerId),
  ]

  if (options?.isRestricted && options.callerUid) {
    const callerUid = options.callerUid
    const unsubs: Unsubscribe[] = []
    const groups: OpsBooking[][] = [[], []]

    const emit = () => {
      const mergedMap = new Map<string, OpsBooking>()
      for (const group of groups) {
        for (const item of group) {
          mergedMap.set(item.id, item)
        }
      }
      const list = Array.from(mergedMap.values())
      list.sort((a, b) => b.startAt - a.startAt)
      onUpdate(list)
    }

    const q1 = query(coll, ...baseConstraints, where('readAccess', '==', null))
    const q2 = query(coll, ...baseConstraints, where('readAccess', 'array-contains', callerUid))

    unsubs.push(
      onSnapshot(
        q1,
        (snap) => {
          groups[0] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<OpsBooking, 'id'>) }))
          emit()
        },
        onError,
      ),
    )
    unsubs.push(
      onSnapshot(
        q2,
        (snap) => {
          groups[1] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<OpsBooking, 'id'>) }))
          emit()
        },
        onError,
      ),
    )

    return () => {
      for (const u of unsubs) u()
    }
  }

  const q = query(coll, ...baseConstraints)

  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<OpsBooking, 'id'>) }))
      items.sort((a, b) => b.startAt - a.startAt)
      onUpdate(items)
    },
    onError,
  )
}

export interface CustomersQueryOptions {
  namePrefix?: string
}

export function subscribeCustomers(
  workspaceId: string,
  sector: OpsSector,
  onUpdate: (items: OpsCustomer[]) => void,
  onError?: (err: Error) => void,
  options?: CustomersQueryOptions,
): Unsubscribe {
  if (!workspaceId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_customers')
  const q = query(
    coll,
    where('workspaceId', '==', workspaceId),
    where('sectors', 'array-contains', sector),
  )

  return onSnapshot(
    q,
    (snap) => {
      let items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<OpsCustomer, 'id'>) }))
      if (options?.namePrefix?.trim()) {
        const prefix = options.namePrefix.trim().toLowerCase()
        items = items.filter((c) => c.displayName.toLowerCase().startsWith(prefix))
      }
      items.sort((a, b) => a.displayName.localeCompare(b.displayName))
      onUpdate(items)
    },
    onError,
  )
}

export async function getCustomerContact(
  workspaceId: string,
  customerId: string,
): Promise<OpsCustomerContact | null> {
  if (!workspaceId || !customerId) return null
  const d = await getDoc(doc(firestoreDb, 'ops_customer_contacts', customerId))
  if (!d.exists()) return null
  return { customerId, ...(d.data() as Omit<OpsCustomerContact, 'customerId'>) }
}

export function subscribeMyCustomerRecord(
  workspaceId: string,
  uid: string,
  onUpdate: (item: OpsCustomer | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId || !uid) {
    onUpdate(null)
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_customers')
  const q = query(
    coll,
    where('workspaceId', '==', workspaceId),
    where('linkedUid', '==', uid),
    limit(1),
  )

  return onSnapshot(
    q,
    (snap) => {
      if (snap.empty) {
        onUpdate(null)
        return
      }
      const d = snap.docs[0]
      onUpdate({ id: d.id, ...(d.data() as Omit<OpsCustomer, 'id'>) })
    },
    onError,
  )
}

export async function getMyCustomerRecord(
  workspaceId: string,
  uid: string,
): Promise<OpsCustomer | null> {
  if (!workspaceId || !uid) return null
  const coll = collection(firestoreDb, 'ops_customers')
  const q = query(
    coll,
    where('workspaceId', '==', workspaceId),
    where('linkedUid', '==', uid),
    limit(1),
  )
  const snap = await getDocs(q)
  if (snap.empty) return null
  const d = snap.docs[0]
  return { id: d.id, ...(d.data() as Omit<OpsCustomer, 'id'>) }
}

export async function refreshAuthIdToken(force = true): Promise<string | null> {
  const { firebaseAuth } = await import('./firebase')
  const user = firebaseAuth.currentUser
  if (!user) return null
  return user.getIdToken(force)
}

export function subscribeMembershipsByCustomer(
  workspaceId: string,
  customerId: string,
  onUpdate: (items: OpsMembership[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId || !customerId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_memberships')
  const q = query(
    coll,
    where('workspaceId', '==', workspaceId),
    where('customerId', '==', customerId),
  )

  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<OpsMembership, 'id'>) }))
      items.sort((a, b) => b.createdAt - a.createdAt)
      onUpdate(items)
    },
    onError,
  )
}

export function subscribeMembershipLedger(
  membershipId: string,
  onUpdate: (items: OpsMembershipLedgerEntry[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!membershipId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_memberships', membershipId, 'ledger')
  const q = query(coll, orderBy('at', 'desc'), limit(100))

  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<OpsMembershipLedgerEntry, 'id'>) }))
      onUpdate(items)
    },
    onError,
  )
}

export function subscribeBookingDuesByCustomer(
  workspaceId: string,
  customerId: string,
  onUpdate: (items: OpsBookingDue[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId || !customerId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_booking_dues')
  const q = query(
    coll,
    where('workspaceId', '==', workspaceId),
    where('customerId', '==', customerId),
  )

  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<OpsBookingDue, 'id'>) }))
      items.sort((a, b) => b.createdAt - a.createdAt)
      onUpdate(items)
    },
    onError,
  )
}

export function subscribeBookingPlans(
  workspaceId: string,
  sector: OpsSector,
  onUpdate: (items: OpsBookingPlan[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_booking_plans')
  const q = query(
    coll,
    where('workspaceId', '==', workspaceId),
    where('sector', '==', sector),
  )

  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<OpsBookingPlan, 'id'>) }))
      items.sort((a, b) => a.name.localeCompare(b.name))
      onUpdate(items)
    },
    onError,
  )
}

export function subscribeBookingPlan(
  planId: string,
  onUpdate: (plan: OpsBookingPlan | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!planId) {
    onUpdate(null)
    return () => {}
  }

  const ref = doc(firestoreDb, 'ops_booking_plans', planId)
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onUpdate(null)
      } else {
        onUpdate({ id: snap.id, ...(snap.data() as Omit<OpsBookingPlan, 'id'>) })
      }
    },
    onError,
  )
}

export function subscribePublicBookingSettings(
  workspaceId: string,
  onUpdate: (settings: OpsPublicBookingSettings | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    onUpdate(null)
    return () => {}
  }

  const ref = doc(firestoreDb, 'ops_public_booking_settings', workspaceId)
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onUpdate(null)
      } else {
        onUpdate(snap.data() as OpsPublicBookingSettings)
      }
    },
    onError,
  )
}

export function subscribeQueueTokens(
  placeId: string,
  dayKey: string,
  onUpdate: (tokens: OpsQueueToken[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!placeId || !dayKey) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_queue_tokens')
  const q = query(
    coll,
    where('placeId', '==', placeId),
    where('dayKey', '==', dayKey),
    orderBy('n', 'asc'),
  )

  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<OpsQueueToken, 'id'>) }))
      onUpdate(items)
    },
    onError,
  )
}
